/**
 * Feature hook: OneDrive list, filtered, delete, download.
 * Preview in Teams uses usePreviewInTeams + previewInTeamsSlice (single API). No preview URL here.
 * downloadOneDriveToLocal: fetches download URL, then opens it so the file saves to the user's machine.
 */
import { useCallback, useMemo, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../hooks";
import {
  fetchOneDriveDocuments,
  deleteOneDriveDocument,
  deleteOneDriveDocumentsBulk,
  fetchOneDriveDownloadUrl,
  setDownloadDialogOpen,
  fetchOneDriveSharingDetails,
  openSharingDetailsDialog,
  closeSharingDetailsDialog,
  stopOneDriveDocumentSharing,
  removeOneDriveSharingLink,
  updateOneDriveSharingRole,
} from "../slices/oneDrive";
import { onDelOneDriveDocument as delOneDriveDocumentAction } from "../slices/documentsAction";
import { logger } from "../../utils/logger";

export function useOneDrive() {
  const dispatch = useAppDispatch();
  const listState = useAppSelector((state) => state.OneDriveDocuments);
  const filteredOneDriveDocuments = useAppSelector((state) => state.FilteredOneDriveDocuments);
  const downloadState = useAppSelector((state) => state.OneDriveDownload);
  const sharingDetailsState = useAppSelector((state) => state.OneDriveSharingDetails);
  const downloadUrl = downloadState?.downloadUrl ?? "";

  const oneDriveDocuments = useMemo(
    () => ({
      ...listState,
      oneDriveDocumentDownloadUrl: downloadUrl,
    }),
    [listState, downloadUrl]
  );

  const fetchOneDriveDocumentsAction = useCallback(
    (teamId, channelId, tenantId, pageSize, searchText, documentType, fileRoot, isOnedrive) =>
      dispatch(fetchOneDriveDocuments({ teamId, channelId, tenantId, pageSize, searchText, documentType, fileRoot, isOnedrive })),
    [dispatch]
  );

  const deleteOneDriveDocumentAction = useCallback(
    (membershipType, fileId, teamId, document, operationType, oneDriveRoot, channelId) =>
      dispatch(deleteOneDriveDocument({ membershipType, fileId, teamId, document, operationType, oneDriveRoot, channelId })),
    [dispatch]
  );

  const deleteOneDriveDocumentsBulkAction = useCallback(
    (membershipType, teamId, channelId, tenantId, oneDriveRoot, documentIds) =>
      dispatch(
        deleteOneDriveDocumentsBulk({
          membershipType,
          teamId,
          channelId,
          tenantId,
          oneDriveRoot,
          documentIds,
        }),
      ),
    [dispatch],
  );

  const delOneDriveDocument = useCallback(
    (fileId, teamId) => dispatch(delOneDriveDocumentAction(fileId, teamId)),
    [dispatch]
  );

  const fetchOneDriveDownloadUrlAction = useCallback(
    (teamId, channelId, tenantId, documentId, fileRoot, operationType, options = {}) =>
      dispatch(
        fetchOneDriveDownloadUrl({
          teamId,
          channelId,
          tenantId,
          documentId,
          fileRoot,
          operationType,
          ...options,
        }),
      ),
    [dispatch]
  );

  const downloadOneDriveToLocal = useCallback(
    async (teamId, channelId, tenantId, documentId, fileRoot, operationType, isLocal = false) => {
      if (!documentId) {
        logger.warn("[useOneDrive] downloadOneDriveToLocal skipped: no documentId", null, "DIALOG");
        return;
      }
      // Only when isLocal: open blank window in same user gesture so the browser doesn't block it
      const downloadWindow = isLocal ? window.open("", "_self", "noopener,noreferrer") : null;
      if (downloadWindow) downloadWindow.opener = null;

      const result = await dispatch(
        fetchOneDriveDownloadUrl({
          teamId,
          channelId,
          tenantId,
          documentId,
          fileRoot,
          operationType
        })
      );
      if (isLocal && result.meta?.requestStatus === "fulfilled" && result.payload && downloadWindow && !downloadWindow.closed) {
        downloadWindow.location.href = result.payload;
      } else if (downloadWindow && !downloadWindow.closed && result.meta?.requestStatus !== "fulfilled") {
        downloadWindow.close();
      }
    },
    [dispatch]
  );

  const closeDownloadDialog = useCallback(() => {
    dispatch(setDownloadDialogOpen(false));
  }, [dispatch]);

  // Several lists (Working Documents, OneDrive/channel documents) render their
  // own Sharing Details dialog off this shared slice. Tag the dialog with the
  // hook instance that opened it so only that one shows (otherwise two stack).
  const sharingOwnerIdRef = useRef(null);
  if (sharingOwnerIdRef.current === null) {
    sharingOwnerIdRef.current = `sharing-${Math.random().toString(36).slice(2)}`;
  }

  const openSharingDetails = useCallback(
    (documentName, iconSrc, isFolder = false) => {
      dispatch(
        openSharingDetailsDialog({
          documentName: documentName ?? "",
          iconSrc: iconSrc ?? "",
          isFolder: isFolder === true,
          ownerId: sharingOwnerIdRef.current,
        }),
      );
    },
    [dispatch],
  );

  const closeSharingDetails = useCallback(() => {
    dispatch(closeSharingDetailsDialog());
  }, [dispatch]);

  const fetchSharingDetails = useCallback(
    (teamId, channelId, tenantId, documentId, fileRoot) =>
      dispatch(
        fetchOneDriveSharingDetails({
          teamId,
          channelId,
          tenantId,
          documentId,
          fileRoot,
        }),
      ),
    [dispatch],
  );

  const stopSharing = useCallback(
    () => dispatch(stopOneDriveDocumentSharing()),
    [dispatch],
  );

  /** Delete a link ({ permissionId }) or remove one person from it (+ recipientEmail / recipientId). */
  const removeSharingLink = useCallback(
    (args) => dispatch(removeOneDriveSharingLink(args)),
    [dispatch],
  );

  /** Change one person's access: { recipientEmail | recipientId, role: "read" | "write" }. */
  const updateSharingRole = useCallback(
    (args) => dispatch(updateOneDriveSharingRole(args)),
    [dispatch],
  );

  return {
    oneDriveDocuments,
    filteredOneDriveDocuments,
    fetchOneDriveDocuments: fetchOneDriveDocumentsAction,
    deleteOneDriveDocument: deleteOneDriveDocumentAction,
    deleteOneDriveDocumentsBulk: deleteOneDriveDocumentsBulkAction,
    delOneDriveDocument,
    fetchOneDriveDownloadUrl: fetchOneDriveDownloadUrlAction,
    downloadOneDriveToLocal,
    closeDownloadDialog,
    downloadState: {
      isOpen: downloadState?.isOpen ?? false,
      loading: downloadState?.loading ?? false,
      isError: downloadState?.isError ?? false,
      message: downloadState?.message ?? "",
    },
    openSharingDetails,
    closeSharingDetails,
    fetchSharingDetails,
    stopSharing,
    removeSharingLink,
    updateSharingRole,
    sharingDetailsState: {
      isOpen:
        (sharingDetailsState?.isOpen ?? false) &&
        sharingDetailsState?.ownerId === sharingOwnerIdRef.current,
      loading: sharingDetailsState?.loading ?? false,
      stopping: sharingDetailsState?.stopping ?? false,
      removingLinkKey: sharingDetailsState?.removingLinkKey ?? "",
      updatingPersonKey: sharingDetailsState?.updatingPersonKey ?? "",
      linkError: sharingDetailsState?.linkError ?? "",
      isError: sharingDetailsState?.isError ?? false,
      message: sharingDetailsState?.message ?? "",
      details: sharingDetailsState?.details ?? null,
      documentName: sharingDetailsState?.documentName ?? "",
      iconSrc: sharingDetailsState?.iconSrc ?? "",
      isFolder: sharingDetailsState?.isFolder ?? false,
    },
  };
}
